import { create } from 'zustand';
import {
  User,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';
import { auth } from '../config/firebase';

// Email sign-in link action code settings
const actionCodeSettings = {
  url: window.location.origin + '/login',
  handleCodeInApp: true,
};

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signInWithApple: () => Promise<void>;
  signInWithEmail: (email: string) => Promise<void>;
  completeEmailSignIn: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  setError: (error: string | null) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: false,
  error: null,
  initialized: false,

  signInWithGoogle: async () => {
    set({ loading: true, error: null });
    try {
      const provider = new GoogleAuthProvider();

      provider.setCustomParameters({
        prompt: 'select_account',
      });
      const result = await signInWithPopup(auth, provider);
      set({ user: result.user, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  signInWithMicrosoft: async () => {
    set({ loading: true, error: null });
    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account',
      });
      const result = await signInWithPopup(auth, provider);
      console.log('Microsoft sign-in result:', result);
      set({ user: result.user, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  signInWithApple: async () => {
    set({ loading: true, error: null });
    try {
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const result = await signInWithPopup(auth, provider);
      set({ user: result.user, loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  signInWithEmail: async (email: string) => {
    set({ loading: true, error: null });
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Store the email in localStorage for later use in email sign-in
      localStorage.setItem('emailForSignIn', email);
      set({ loading: false });
      return Promise.resolve();
    } catch (error: any) {
      set({ error: error.message, loading: false });
      return Promise.reject(error);
    }
  },

  completeEmailSignIn: async (email: string) => {
    set({ loading: true, error: null });
    try {
      // Check if current URL contains the sign-in link
      if (isSignInWithEmailLink(auth, window.location.href)) {
        // Sign in the user with the email link
        const result = await signInWithEmailLink(auth, email, window.location.href);
        // Clear email from storage
        localStorage.removeItem('emailForSignIn');
        // Remove the query parameters from URL
        window.history.replaceState(null, '', window.location.pathname);
        set({ user: result.user, loading: false });
      } else {
        set({ loading: false });
      }
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  signOut: async () => {
    set({ loading: true, error: null });
    try {
      await signOut(auth);
      set({ user: null, loading: false });
      window.location.reload(); // Reload the page to reflect the sign-out
    } catch (error: any) {
      set({ error: error.message, loading: false });
    }
  },

  setError: (error: string | null) => set({ error }),
}));

onAuthStateChanged(auth, (user) => {
  useAuthStore.setState({ user, initialized: true });
});
